import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Plus, Calendar, IndianRupee, Check, X, Clock, ArrowRight, Send, AlertCircle, Download } from 'lucide-react';
import { API } from '@/App';
import { formatDate } from '@/utils/dateFormat';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('payments');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canManage = user.role === 'owner' || user.role === 'staff';
  const canRequestPayment = user.role === 'vendor' || user.role === 'labour' || canManage;

  const [formData, setFormData] = useState({
    project_id: '',
    amount: '',
    status: 'pending',
    due_date: '',
    invoice_number: '',
    description: '',
  });

  const [requestFormData, setRequestFormData] = useState({
    project_id: '',
    amount: '',
    request_type: user.role === 'vendor' || user.role === 'labour' ? 'vendor_to_company' : 'company_to_client',
    description: '',
    milestone: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [paymentsRes, projectsRes, requestsRes] = await Promise.all([
        axios.get(`${API}/payments`),
        axios.get(`${API}/projects`).catch(() => ({ data: [] })),
        axios.get(`${API}/payment-requests`).catch(() => ({ data: [] })),
      ]);
      setPayments(paymentsRes.data);
      setProjects(projectsRes.data);
      setPaymentRequests(requestsRes.data);
    } catch (error) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/payments`, {
        ...formData,
        amount: parseFloat(formData.amount),
        due_date: new Date(formData.due_date).toISOString(),
      });
      toast.success('Payment record created successfully');
      setDialogOpen(false);
      fetchData();
      setFormData({
        project_id: '',
        amount: '',
        status: 'pending',
        due_date: '',
        invoice_number: '',
        description: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payment record');
    }
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/payment-requests`, {
        ...requestFormData,
        amount: parseFloat(requestFormData.amount),
      });
      toast.success('Payment request submitted successfully');
      setRequestDialogOpen(false);
      fetchData();
      setRequestFormData({
        project_id: '',
        amount: '',
        request_type: user.role === 'vendor' || user.role === 'labour' ? 'vendor_to_company' : 'company_to_client',
        description: '',
        milestone: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit payment request');
    }
  };

  const handleApproveRequest = async (requestId) => {
    try {
      await axios.put(`${API}/payment-requests/${requestId}/approve`);
      toast.success('Payment request approved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    }
  };

  // iter-338s: Download per-transaction PDF receipt. Uses existing
  // /api/payments/{id}/receipt-pdf endpoint and triggers a blob download.
  const handleDownloadReceipt = async (paymentId, invoiceNumber) => {
    try {
      toast.loading('Generating receipt…', { id: `pay-rcpt-${paymentId}` });
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/payments/${paymentId}/receipt-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`(${res.status}) ${txt.slice(0, 100)}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Receipt_${invoiceNumber || paymentId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Receipt downloaded', { id: `pay-rcpt-${paymentId}` });
    } catch (e) {
      toast.error(`Download failed: ${e.message || e}`, { id: `pay-rcpt-${paymentId}` });
    }
  };

  const handleRejectRequest = async (requestId) => {
    const reason = prompt('Enter rejection reason (optional):');
    try {
      await axios.put(`${API}/payment-requests/${requestId}/reject?reason=${encodeURIComponent(reason || '')}`);
      toast.success('Payment request rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    }
  };

  const handleMarkPaid = async (requestId) => {
    try {
      await axios.put(`${API}/payment-requests/${requestId}/mark-paid`);
      toast.success('Payment marked as paid');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    }
  };

  const getStatusClass = (status) => {
    const classes = {
      pending: 'bg-amber-100 text-amber-700',
      approved: 'bg-blue-100 text-blue-700',
      rejected: 'bg-red-100 text-red-700',
      paid: 'bg-green-100 text-green-700',
      overdue: 'bg-red-100 text-red-700',
    };
    return classes[status] || 'bg-stone-100 text-stone-600';
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || 'Unknown Project';
  };

  const getTotalAmount = () => {
    return payments.reduce((sum, p) => sum + p.amount, 0);
  };

  const getPendingAmount = () => {
    return payments.filter((p) => p.status === 'pending').reduce((sum, p) => sum + p.amount, 0);
  };

  const getPendingRequestsCount = () => {
    return paymentRequests.filter(r => r.status === 'pending').length;
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12" data-testid="loading-state">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="payments-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="font-playfair text-4xl font-bold text-stone-900 mb-2" data-testid="payments-title">Payments</h1>
            <p className="text-stone-600">Track invoices, payments, and payment requests</p>
          </div>
          <div className="flex gap-3">
            {/* Request Payment Button - for vendors/labour or staff requesting from client */}
            {canRequestPayment && (
              <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="flex items-center gap-2" data-testid="request-payment-button">
                    <Send size={18} />
                    Request Payment
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-playfair text-2xl">Request Payment</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleRequestSubmit} className="space-y-4">
                    <div>
                      <Label>Project *</Label>
                      <Select
                        value={requestFormData.project_id}
                        onValueChange={(value) => setRequestFormData({ ...requestFormData, project_id: value })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select project" />
                        </SelectTrigger>
                        <SelectContent>
                          {projects.map((p) => (
                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {canManage && (
                      <div>
                        <Label>Request Type *</Label>
                        <Select
                          value={requestFormData.request_type}
                          onValueChange={(value) => setRequestFormData({ ...requestFormData, request_type: value })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="company_to_client">Request from Client</SelectItem>
                            <SelectItem value="vendor_to_company">Internal Request (Vendor/Labour)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div>
                      <Label>Amount (₹) *</Label>
                      <Input
                        type="number"
                        required
                        value={requestFormData.amount}
                        onChange={(e) => setRequestFormData({ ...requestFormData, amount: e.target.value })}
                        placeholder="Enter amount"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label>Milestone / Reason</Label>
                      <Input
                        value={requestFormData.milestone}
                        onChange={(e) => setRequestFormData({ ...requestFormData, milestone: e.target.value })}
                        placeholder="e.g., Foundation Complete, 50% Work Done"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label>Description *</Label>
                      <Textarea
                        required
                        value={requestFormData.description}
                        onChange={(e) => setRequestFormData({ ...requestFormData, description: e.target.value })}
                        placeholder="Describe the work completed or reason for payment"
                        className="mt-1"
                        rows={3}
                      />
                    </div>

                    <div className="flex gap-3 justify-end pt-4">
                      <Button type="button" variant="outline" onClick={() => setRequestDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="apple-button">
                        Submit Request
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}

            {/* New Payment Button - for staff only */}
            {canManage && (
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary flex items-center gap-2" data-testid="create-payment-button">
                    <Plus size={20} />
                    New Payment
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl" data-testid="create-payment-dialog">
                  <DialogHeader>
                    <DialogTitle className="font-playfair text-2xl">Create Payment Record</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label>Project</Label>
                      <Select
                        value={formData.project_id}
                        onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                      >
                        <SelectTrigger className="mt-1" data-testid="project-select">
                          <SelectValue placeholder="Select project" />
                        </SelectTrigger>
                        <SelectContent>
                          {projects.map((p) => (
                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Invoice Number</Label>
                        <Input
                          required
                          value={formData.invoice_number}
                          onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                          placeholder="INV-001"
                          className="mt-1"
                          data-testid="invoice-number-input"
                        />
                      </div>
                      <div>
                        <Label>Amount (₹)</Label>
                        <Input
                          type="number"
                          required
                          value={formData.amount}
                          onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                          className="mt-1"
                          data-testid="amount-input"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Due Date</Label>
                        <Input
                          type="date"
                          required
                          value={formData.due_date}
                          onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                          className="mt-1"
                          data-testid="due-date-input"
                        />
                      </div>
                      <div>
                        <Label>Status</Label>
                        <Select
                          value={formData.status}
                          onValueChange={(value) => setFormData({ ...formData, status: value })}
                        >
                          <SelectTrigger className="mt-1" data-testid="status-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="paid">Paid</SelectItem>
                            <SelectItem value="overdue">Overdue</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Payment details..."
                        className="mt-1"
                        data-testid="description-input"
                      />
                    </div>

                    <div className="flex gap-3 justify-end pt-4">
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="apple-button" data-testid="submit-payment-button">
                        Create Payment
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="apple-card p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <IndianRupee size={20} className="text-green-600" />
              </div>
              <span className="text-sm text-stone-500">Total Payments</span>
            </div>
            <p className="text-2xl font-bold text-stone-900">₹{getTotalAmount().toLocaleString()}</p>
          </div>
          <div className="apple-card p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock size={20} className="text-amber-600" />
              </div>
              <span className="text-sm text-stone-500">Pending Payments</span>
            </div>
            <p className="text-2xl font-bold text-stone-900">₹{getPendingAmount().toLocaleString()}</p>
          </div>
          <div className="apple-card p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Send size={20} className="text-blue-600" />
              </div>
              <span className="text-sm text-stone-500">Payment Requests</span>
            </div>
            <p className="text-2xl font-bold text-stone-900">{paymentRequests.length}</p>
          </div>
          <div className="apple-card p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertCircle size={20} className="text-red-600" />
              </div>
              <span className="text-sm text-stone-500">Pending Approval</span>
            </div>
            <p className="text-2xl font-bold text-stone-900">{getPendingRequestsCount()}</p>
          </div>
        </div>

        {/* Tabs for Payments and Requests */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 bg-stone-100">
            <TabsTrigger value="payments" className="text-stone-900 data-[state=active]:bg-white">
              Payments ({payments.length})
            </TabsTrigger>
            <TabsTrigger value="requests" className="text-stone-900 data-[state=active]:bg-white">
              Payment Requests ({paymentRequests.length})
              {getPendingRequestsCount() > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                  {getPendingRequestsCount()}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Payments Tab */}
          <TabsContent value="payments">
            {payments.length === 0 ? (
              <div className="apple-card p-12 text-center">
                <IndianRupee size={48} className="mx-auto text-stone-300 mb-4" />
                <h3 className="text-lg font-medium text-stone-600 mb-2">No Payments Yet</h3>
                <p className="text-stone-500">Create your first payment record to get started</p>
              </div>
            ) : (
              <div className="apple-card overflow-hidden">
                <table className="w-full">
                  <thead className="bg-stone-50 border-b border-stone-200">
                    <tr>
                      <th className="text-left px-6 py-4 text-sm font-medium text-stone-500">Invoice</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-stone-500">Project</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-stone-500">Amount</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-stone-500">Due Date</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-stone-500">Status</th>
                      <th className="text-right px-6 py-4 text-sm font-medium text-stone-500">Receipt</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {payments.map((payment) => (
                      <tr key={payment.id} className="hover:bg-stone-50" data-testid={`payment-${payment.id}`}>
                        <td className="px-6 py-4 font-medium text-stone-900">{payment.invoice_number}</td>
                        <td className="px-6 py-4 text-stone-600">{getProjectName(payment.project_id)}</td>
                        <td className="px-6 py-4 font-semibold text-stone-900">₹{payment.amount.toLocaleString()}</td>
                        <td className="px-6 py-4 text-stone-600">
                          {formatDate(payment.due_date)}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusClass(payment.status)}`}>
                            {payment.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadReceipt(payment.id, payment.invoice_number)}
                            className="text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                            data-testid={`payment-receipt-pdf-${payment.id}`}
                            title="Download transaction receipt PDF"
                          >
                            <Download size={14} className="mr-1" />
                            PDF
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Payment Requests Tab */}
          <TabsContent value="requests">
            {paymentRequests.length === 0 ? (
              <div className="apple-card p-12 text-center">
                <Send size={48} className="mx-auto text-stone-300 mb-4" />
                <h3 className="text-lg font-medium text-stone-600 mb-2">No Payment Requests</h3>
                <p className="text-stone-500">
                  {canRequestPayment 
                    ? "Request a payment to get started" 
                    : "No payment requests have been submitted yet"}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {paymentRequests.map((request) => (
                  <div 
                    key={request.id} 
                    className="apple-card p-6"
                    data-testid={`payment-request-${request.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            request.request_type === 'vendor_to_company' 
                              ? 'bg-amber-100 text-amber-700' 
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {request.request_type === 'vendor_to_company' ? 'Vendor → Company' : 'Company → Client'}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusClass(request.status)}`}>
                            {request.status}
                          </span>
                        </div>
                        <h3 className="font-semibold text-stone-900 text-lg">₹{request.amount.toLocaleString()}</h3>
                        <p className="text-sm text-stone-600 mt-1">{getProjectName(request.project_id)}</p>
                        {request.milestone && (
                          <p className="text-sm text-stone-500 mt-1">
                            <span className="font-medium">Milestone:</span> {request.milestone}
                          </p>
                        )}
                        <p className="text-sm text-stone-500 mt-1">{request.description}</p>
                        <div className="flex items-center gap-4 mt-3 text-xs text-stone-400">
                          <span>Requested by: {request.requested_by_name}</span>
                          <span>{formatDate(request.created_at)}</span>
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      {canManage && request.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleApproveRequest(request.id)}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <Check size={16} className="mr-1" />
                            Approve
                          </Button>
                          <Button
                            onClick={() => handleRejectRequest(request.id)}
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <X size={16} className="mr-1" />
                            Reject
                          </Button>
                        </div>
                      )}
                      
                      {canManage && request.status === 'approved' && (
                        <Button
                          onClick={() => handleMarkPaid(request.id)}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <IndianRupee size={16} className="mr-1" />
                          Mark as Paid
                        </Button>
                      )}
                      
                      {request.status === 'rejected' && request.rejection_reason && (
                        <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
                          <span className="font-medium">Reason:</span> {request.rejection_reason}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
